import { useMemo } from 'react';
import { groupOf } from '../state/emptyDoc';
import { splitVideoFields } from '../fields/videoLayout';
import { usePreferences } from '../state/usePreferences';

const clamp01 = (n) => Math.max(0, Math.min(1, n));

// How far one field is toward being answered. Only required fields count
// toward a section's completion.
const fieldPct = (doc, section, field) => {
  const value = groupOf(doc, section.key)[field.key];
  if (field.kind === 'list') {
    const n = (value || []).length;
    return field.max_picks ? clamp01(n / field.max_picks) : clamp01(n);
  }
  // A choice is complete the moment either option is pressed: "no" is an
  // answer, not an empty section.
  return String(value === undefined || value === null ? '' : value).trim() ? 1 : 0;
};

/**
 * One progress step per section, in payload order, labelled by `section.title`.
 * Optional sections are excluded from the count.
 */
export const useProgress = () => {
  const { doc, sections, index, tab } = usePreferences();

  return useMemo(() => {
    const locked = !!doc.submitted;

    const steps = sections.map((section, i) => {
      // `display` fields are copy, not answers — they never move the bar.
      const fields = (section.fields || []).filter((f) => f.kind !== 'display');
      const required = fields.filter((f) => f.required);
      // What the bar measures: the required fields, or everything when a
      // section asks nothing in particular. Either way it reflects answers,
      // never how far the client has walked.
      const measured = required.length ? required : fields;
      // A section with two required fields shows two segments — Videos does.
      // A segment counts as passed once the client is beyond it — including
      // beyond it *within* a section, so moving from Highlights to Reels
      // fills the Highlights half rather than only moving the marker.
      const passed = (si) =>
        locked || i < index || (i === index && si < (tab || 0));
      // A segment is a tab, not a field. Only a tabbed section has a second
      // place for the client to be, so anywhere else the required fields
      // share one segment: splitting a flat section would strand the marker
      // in the first slice and leave the rest unreachable, because `tab`
      // never leaves 0 there.
      const sliced = splitVideoFields(section).isTabbed && measured.length > 1;
      // A submitted form reads as finished. The server took it with whatever
      // each required field held, so the bar has nothing left to ask for and
      // showing it short would argue with a submit already accepted.
      const pcts = measured.map((f) => (locked ? 1 : fieldPct(doc, section, f)));
      const segments = !measured.length
        ? [{ pct: locked ? 1 : 0, past: passed(0) }]
        : sliced
        ? measured.map((f, si) => ({ pct: pcts[si], past: passed(si) }))
        : [{
            pct: pcts.reduce((a, b) => a + b, 0) / pcts.length,
            past: passed(0),
          }];
      const touched = fields.some((f) => fieldPct(doc, section, f) > 0);
      // Only a required field can leave a section unfinished. Where the server
      // marks none, the bar still fills as answers arrive, but a blank field is
      // a choice the client made, not something missing.
      const done = locked || (required.length
        ? segments.every((s) => s.pct >= 1)
        : touched);

      return {
        key: section.key,
        label: section.title,
        optional: !!section.optional,
        // Whether anything here can actually be missing.
        blocking: !!required.length,
        index: i,
        segments,
        done,
        // Never failed: nothing in this bar is ever shown in red.
        failed: false,
        current: i === index,
        dim: !(i <= index || touched),
      };
    });

    // The marker rides the whole row, so it is resolved against the real column
    // geometry rather than a flat percentage.
    const markerLeft = (isPhone) => {
      const gap = isPhone ? 6 : 14;
      const pad = isPhone ? 3 : 0;
      const count = steps.length || 1;
      const idx = locked ? count - 1 : index;
      const here = steps[idx];
      // In a section with two segments the marker sits in the segment the
      // client is actually on, so switching tabs moves it.
      const frac = locked
        ? 1
        : here
        ? clamp01(
            (Math.min(tab || 0, here.segments.length - 1) +
              (here.segments[Math.min(tab || 0, here.segments.length - 1)]?.pct || 0)) /
              here.segments.length
          )
        : 0;
      const off = gap * idx + pad - 2 * pad * frac;
      return `calc((100% - ${gap * (count - 1)}px) / ${count} * ${idx + frac} + ${off.toFixed(2)}px)`;
    };

    // What the submit gate warns about: sections the server will refuse, not
    // optional fields the client chose to leave blank.
    const incomplete = steps
      .filter((s) => s.blocking && !s.optional && !s.done)
      .map((s) => s.label);

    return {
      steps,
      markerLeft,
      positionLabel: steps[index] ? `Section ${index + 1} of ${steps.length}` : '',
      allComplete: incomplete.length === 0,
      incompleteLabels: incomplete,
    };
  }, [doc, sections, index, tab]);
};
